'use client'

import { useRef, useEffect, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { testimonials } from '../data/testimonials'
import { voicesStore } from '../store/voicesStore'

gsap.registerPlugin(ScrollTrigger, useGSAP)

/**
 * SCENE 06: THE VOICES — "TRANSMISSIONS"
 * Testimonials arrive as intercepted signals on a transmission console: the quote
 * DECODES from noise (blur → sharp), a live waveform pulses as the source speaks, a
 * signal-strength meter reads out, and a channel rail tunes between sources. The HUD
 * chrome (corner brackets, mono header, status readout) is shared with the Gallery's
 * Forge Terminal so the site reads as one coherent machine. Pin/scroll geometry is
 * unchanged so the HexField quotation-mark phase still fires.
 */
export default function Voices() {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const waveRef = useRef<HTMLCanvasElement>(null)
  const activeRef = useRef(-1)
  const burstRef = useRef(0)

  // ── TUNING STATE — one shared 'detune' scalar (0 clean → 1 broadband static)
  //    drives the quote blur/RGB-tear, the canvas noise, the rail playhead and
  //    the frequency counter in unison across each boundary crossover. ──
  const detuneRef = useRef(0)
  const railRef = useRef<HTMLDivElement>(null)
  const playheadRef = useRef<HTMLDivElement>(null)
  const nodeYRef = useRef<number[]>([])
  const freqRef = useRef<HTMLSpanElement>(null)
  const reduceRef = useRef(false)
  const [reduced, setReduced] = useState(false)

  // Each source occupies a distinct FM station; the counter tunes between them.
  const freqsRef = useRef<number[] | null>(null)
  if (!freqsRef.current) {
    const n = Math.max(1, testimonials.length - 1)
    freqsRef.current = testimonials.map((_, i) => 88.1 + (i * 19.6) / n)
  }

  // Reduced-motion: mirror to a ref (read inside rAF / onUpdate) and to state
  // (gates the inline CSS animations at render). Kept in sync live.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => { reduceRef.current = mq.matches; setReduced(mq.matches) }
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const highlightQuote = (quote: string, highlights: string[]) => {
    let result = quote
    highlights.forEach((phrase) => {
      result = result.replace(phrase, `<span style="color: var(--color-fire)">${phrase}</span>`)
    })
    return result
  }
  const quotesHTML = testimonials.map((t) => `“${highlightQuote(t.quote, t.highlights)}”`)

  // ── VOICEPRINTS — each source's waveform envelope is derived from its ACTUAL
  //    words (char codes sampled across the quote), so every transmission has a
  //    distinct, repeatable signature instead of identical sine noise. ──
  const printsRef = useRef<Float32Array[] | null>(null)
  if (!printsRef.current) {
    printsRef.current = testimonials.map((tst) => {
      const txt = tst.quote
      const bars = 72
      const out = new Float32Array(bars)
      for (let i = 0; i < bars; i++) {
        const c1 = txt.charCodeAt(Math.floor((i / bars) * txt.length)) || 32
        const c2 = txt.charCodeAt(Math.floor(((i + 0.5) / bars) * txt.length)) || 32
        out[i] = 0.25 + (((c1 * 31 + c2 * 17) % 97) / 97) * 0.75
      }
      return out
    })
  }

  // Per-source signal-strength meter heights (seeded from the source's name —
  // deterministic, distinct per voice; was one hardcoded array for all).
  const meterBars = (seed: string) =>
    [0, 1, 2, 3, 4].map((b) => 0.35 + ((seed.charCodeAt((b * 3) % seed.length) * 13 + b * 29) % 61) / 95)

  // ── Live waveform — desktop only, paused off-screen, bursts on new signal ──
  useEffect(() => {
    const canvas = waveRef.current
    const host = containerRef.current
    if (!canvas || !host) return
    if (window.innerWidth < 768) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    let raf = 0
    let t = 0
    let running = false
    const resize = () => { canvas.width = canvas.clientWidth * dpr; canvas.height = canvas.clientHeight * dpr }
    resize()
    window.addEventListener('resize', resize)

    const draw = () => {
      const w = canvas.width
      const h = canvas.height
      ctx.clearRect(0, 0, w, h)
      const bars = 72
      const gap = 3 * dpr
      const bw = (w - gap * (bars - 1)) / bars
      const energy = 0.32 + burstRef.current * 0.68
      const detune = detuneRef.current
      const print = printsRef.current?.[voicesStore.index >= 0 ? voicesStore.index % (printsRef.current.length || 1) : 0]
      for (let i = 0; i < bars; i++) {
        const u = i / bars
        // The quote's own voiceprint is the envelope; time only makes it breathe.
        const vp = print ? print[i] : 1
        let a = vp * (0.5 + 0.35 * Math.abs(Math.sin(u * 9 + t * 2.1)) + 0.15 * Math.abs(Math.sin(u * 23 - t * 3.3)))
        a *= energy * (0.5 + 0.5 * Math.sin(u * Math.PI))
        // Across the crossover the clean print smears into broadband white noise,
        // then snaps back to the incoming source's print as detune relaxes to 0.
        if (detune > 0) a = a * (1 - detune * 0.82) + (0.25 + Math.random() * 0.9) * detune * energy
        const bh = Math.min(h, Math.max(1.5 * dpr, a * h * 0.92))
        const x = i * (bw + gap)
        const y = (h - bh) / 2
        // Ember bars wash toward white static at the peak of the tune.
        const g = (107 + 148 * detune * 0.9) | 0
        const bl = (255 * detune * 0.9) | 0
        ctx.fillStyle = `rgba(255,${g},${bl},${0.22 + Math.min(a, 1) * 0.6})`
        ctx.fillRect(x, y, bw, bh)
      }
      burstRef.current *= 0.94
    }
    const loop = () => { if (!running) return; t += 0.016; draw(); raf = requestAnimationFrame(loop) }
    const io = new IntersectionObserver(([e]) => {
      running = e.isIntersecting
      if (running) { cancelAnimationFrame(raf); loop() } else cancelAnimationFrame(raf)
    }, { rootMargin: '0px' })
    io.observe(host)

    return () => { io.disconnect(); cancelAnimationFrame(raf); window.removeEventListener('resize', resize) }
  }, [])

  useGSAP(
    () => {
      if (!containerRef.current || !viewportRef.current) return
      const voiceEls = viewportRef.current.querySelectorAll<HTMLElement>('.voice-item')
      const dialEls = containerRef.current.querySelectorAll<HTMLElement>('.dial-node')
      const total = voiceEls.length
      const isMobile = window.innerWidth < 768
      const freqs = freqsRef.current!

      // Measure each dial-node's vertical centre within the rail so the playhead
      // can travel between stations. Re-measured on resize (rail geometry moves).
      const measureNodes = () => {
        const rail = railRef.current
        if (!rail) return
        nodeYRef.current = Array.from(dialEls).map((d) => d.offsetTop + d.offsetHeight / 2)
        if (playheadRef.current && nodeYRef.current.length) {
          const y = nodeYRef.current[Math.max(0, activeRef.current)] ?? nodeYRef.current[0]
          playheadRef.current.style.transform = `translateY(${y}px)`
        }
      }
      measureNodes()
      window.addEventListener('resize', measureNodes)
      ScrollTrigger.addEventListener('refreshInit', measureNodes)

      ScrollTrigger.create({
        trigger: containerRef.current,
        start: 'top top',
        end: `+=${total * 100}%`,
        pin: viewportRef.current,
        scrub: 0.5,
        onUpdate: (self) => {
          const raw = self.progress * total
          const currentIndex = Math.min(Math.floor(raw), total - 1)
          const localP = raw - currentIndex
          const reduce = reduceRef.current

          // ── ONE shared detune scalar (0 clean → 1 broadband static), peaking at
          //    each boundary: ramps up over the tail of the current station
          //    (localP 0.8→1) and relaxes over the head of the next (0→0.15).
          //    Reduced motion holds it flat at 0 → instant, silent swaps. ──
          let detune = 0
          let fromN = currentIndex
          let toN = currentIndex
          let tune = 0 // 0 at 'from' node, 1 at 'to' node — drives the playhead
          if (!reduce) {
            if (localP > 0.8 && currentIndex < total - 1) {
              detune = (localP - 0.8) / 0.2
              fromN = currentIndex
              toN = currentIndex + 1
              tune = detune * 0.5
            } else if (localP < 0.15 && currentIndex > 0) {
              detune = 1 - localP / 0.15
              fromN = currentIndex - 1
              toN = currentIndex
              tune = 1 - detune * 0.5
            }
          }
          detuneRef.current = detune

          // New signal acquired → snap store/waveform + lock the active dial-node.
          if (currentIndex !== activeRef.current) {
            activeRef.current = currentIndex
            voicesStore.index = currentIndex // HexField swaps the particle shape per voice
            burstRef.current = 1
            dialEls.forEach((d, di) => {
              const on = di === currentIndex
              d.style.opacity = on ? '1' : '0.32'
              const tick = d.querySelector('.dial-tick') as HTMLElement | null
              if (tick) tick.style.width = on ? 'clamp(28px, 4vw, 56px)' : 'clamp(14px, 2vw, 24px)'
              const poly = d.querySelector('.dial-hex polygon') as SVGElement | null
              if (poly) poly.setAttribute('fill', on ? 'var(--color-fire)' : 'var(--color-ash)')
              const hex = d.querySelector('.dial-hex') as HTMLElement | null
              if (on && hex && !reduce) {
                gsap.fromTo(
                  hex,
                  { scale: 1 },
                  { scale: 1.45, duration: 0.28, yoyo: true, repeat: 1, transformOrigin: '50% 50%', ease: 'power2.out' },
                )
              }
            })
          }

          // Playhead glides down the rail between the two stations; frequency
          // readout counts across the FM band as it tunes.
          const ys = nodeYRef.current
          if (playheadRef.current && ys.length) {
            const a = ys[fromN] ?? ys[currentIndex] ?? 0
            const b = ys[toN] ?? a
            const y = reduce ? (ys[currentIndex] ?? a) : a + (b - a) * tune
            playheadRef.current.style.transform = `translateY(${y}px)`
            playheadRef.current.style.opacity = String(0.5 + detune * 0.5)
          }
          if (freqRef.current) {
            const cur = detune > 0.01 && fromN !== toN ? freqs[fromN] + (freqs[toN] - freqs[fromN]) * tune : freqs[currentIndex]
            freqRef.current.textContent = `${cur.toFixed(1)} MHz`
          }

          voiceEls.forEach((voice, i) => {
            const quoteEl = voice.querySelector('.voice-quote') as HTMLElement
            const metaEl = voice.querySelector('.voice-meta') as HTMLElement
            const authorEl = voice.querySelector('.voice-author') as HTMLElement
            if (i === currentIndex) {
              voice.style.opacity = '1'
              voice.style.zIndex = '2'
              const entryP = Math.min(localP * 3, 1)
              // The words dissolve into noise at the peak of the tune, then re-lock.
              const dim = 1 - detune * 0.4
              if (metaEl) metaEl.style.opacity = String(entryP * dim)
              if (quoteEl) {
                quoteEl.style.opacity = String(Math.min(entryP * 1.2, 1) * dim)
                if (reduce || isMobile || detune < 0.01) {
                  quoteEl.style.setProperty('--vb', '0px')
                  quoteEl.style.textShadow = 'none'
                  quoteEl.style.transform = 'none'
                } else {
                  // blur → high + horizontal RGB-split / scanline tear
                  quoteEl.style.setProperty('--vb', `${detune * 15}px`)
                  const sp = detune * 7
                  quoteEl.style.textShadow = `${sp}px 0 rgba(255,90,0,0.55), ${-sp}px 0 rgba(80,180,255,0.45)`
                  quoteEl.style.transform = `translateX(${(Math.random() - 0.5) * detune * 6}px)`
                }
              }
              if (authorEl) {
                const ap = Math.max(0, (localP - 0.25) * 2)
                authorEl.style.opacity = String(Math.min(ap, 1) * dim)
                authorEl.style.transform = `translateY(${Math.max(0, (1 - ap) * 12)}px)`
              }
            } else {
              voice.style.opacity = '0'
              voice.style.zIndex = '1'
            }
          })
        },
      })

      return () => {
        window.removeEventListener('resize', measureNodes)
        ScrollTrigger.removeEventListener('refreshInit', measureNodes)
      }
    },
    { scope: containerRef },
  )

  const edge = 'clamp(1.5rem, 4vw, 5rem)'
  const cornerBase = { position: 'absolute' as const, width: 16, height: 16, opacity: 0.5, zIndex: 9 }

  return (
    <section
      ref={containerRef}
      id="voices"
      data-scene="voices"
      className="relative w-full"
      style={{ backgroundColor: 'var(--color-night)' }}
    >
      <div ref={viewportRef} className="relative w-full overflow-hidden" style={{ height: '100dvh' }}>
        {/* CRT scanlines */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none hidden md:block"
          style={{ zIndex: 4, background: 'repeating-linear-gradient(0deg, rgba(255,107,0,0.04) 0px, rgba(255,107,0,0.04) 1px, transparent 2px, transparent 4px)', animation: reduced ? 'none' : 'signal-flicker 6s ease-in-out infinite', mixBlendMode: 'screen' }}
        />

        {/* HUD corner brackets — shared console language with the Gallery */}
        <span aria-hidden className="hidden md:block" style={{ ...cornerBase, top: edge, left: edge, borderTop: '1px solid var(--color-fire)', borderLeft: '1px solid var(--color-fire)' }} />
        <span aria-hidden className="hidden md:block" style={{ ...cornerBase, top: edge, right: edge, borderTop: '1px solid var(--color-fire)', borderRight: '1px solid var(--color-fire)' }} />
        <span aria-hidden className="hidden md:block" style={{ ...cornerBase, bottom: edge, left: edge, borderBottom: '1px solid var(--color-fire)', borderLeft: '1px solid var(--color-fire)' }} />
        <span aria-hidden className="hidden md:block" style={{ ...cornerBase, bottom: edge, right: edge, borderBottom: '1px solid var(--color-fire)', borderRight: '1px solid var(--color-fire)' }} />

        {/* HUD header — section label + live status (coherent with Forge Terminal) */}
        <div className="absolute flex items-center justify-center lg:justify-between gap-4" style={{ zIndex: 10, top: 'clamp(4.5rem, 8vh, 6.5rem)', left: edge, right: edge }}>
          <div className="flex items-center gap-3">
            <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>07</span>
            <svg width={10} height={10} viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
              <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" fill="none" stroke="var(--color-fire)" strokeWidth="8" />
            </svg>
            <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>The Voices</span>
            <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-stone)' }}>/ transmissions</span>
          </div>
          <div className="hidden lg:flex items-center gap-3 font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-stone)' }}>
            <span ref={freqRef} style={{ color: 'var(--color-fire)', minWidth: '5.5em', textAlign: 'right' }}>{freqsRef.current![0].toFixed(1)} MHz</span>
            <span aria-hidden style={{ width: 1, height: 10, background: 'var(--color-border)' }} />
            <span>{String(testimonials.length).padStart(2, '0')} SOURCES</span>
            <span aria-hidden style={{ width: 1, height: 10, background: 'var(--color-border)' }} />
            <span className="flex items-center gap-1.5"><span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-fire)', boxShadow: '0 0 8px var(--color-fire)', animation: reduced ? 'none' : 'dot-pulse 2s ease-in-out infinite' }} />RECEIVING</span>
          </div>
        </div>

        {/* Channel rail — tune between sources (matches the Gallery selector) */}
        <div ref={railRef} className="absolute hidden md:flex flex-col items-end gap-3.5" style={{ zIndex: 10, top: '50%', right: edge, transform: 'translateY(-50%)' }}>
          {/* Tuning playhead — glides between stations across each crossover */}
          <div
            ref={playheadRef}
            aria-hidden="true"
            className="pointer-events-none"
            style={{ position: 'absolute', left: -22, top: -0.5, width: 16, height: 1, background: 'linear-gradient(90deg, transparent, var(--color-fire))', boxShadow: '0 0 6px var(--color-fire)', opacity: 0.5, willChange: 'transform' }}
          />
          {testimonials.map((t, i) => (
            <div key={t.id} className="dial-node flex items-center gap-2.5 transition-opacity duration-300" style={{ opacity: i === 0 ? 1 : 0.32 }}>
              <span className="font-mono" style={{ fontSize: '0.55rem', letterSpacing: '0.18em', color: 'var(--color-stone)' }}>{String(i + 1).padStart(2, '0')}</span>
              <div className="dial-tick" style={{ width: i === 0 ? 'clamp(28px, 4vw, 56px)' : 'clamp(14px, 2vw, 24px)', height: '1px', background: 'var(--color-fire)', transition: 'width .5s var(--ease-out-quart)' }} />
              <svg className="dial-hex" width={8} height={9} viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
                <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" fill={i === 0 ? 'var(--color-fire)' : 'var(--color-ash)'} />
              </svg>
            </div>
          ))}
        </div>

        {/* Live waveform — the incoming signal, sitting directly under the active quote */}
        <canvas
          ref={waveRef}
          aria-hidden="true"
          className="absolute pointer-events-none hidden md:block"
          style={{ left: edge, top: 'calc(50% + clamp(3.5rem, 9vh, 6.5rem))', width: 'min(52vw, 620px)', height: '92px', opacity: 0.5, zIndex: 1 }}
        />

        {/* Transmissions */}
        {testimonials.map((testimonial, i) => (
          <div
            key={testimonial.id}
            className="voice-item absolute inset-0 w-full h-full flex flex-col justify-center items-center lg:items-start opacity-0"
            style={{ paddingLeft: edge, paddingRight: edge, zIndex: 5 }}
          >
            <div className="voice-content">
              {/* Signal HUD tag */}
              <div className="voice-meta flex items-center justify-center lg:justify-start gap-4 mb-7 opacity-0 font-mono uppercase">
                <span className="flex items-center gap-1.5" style={{ fontSize: 'clamp(0.5rem, 0.75vw, 0.62rem)', letterSpacing: '0.24em', color: 'var(--color-fire)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-fire)', animation: reduced ? 'none' : 'blink 1.4s step-end infinite', display: 'inline-block' }} />
                  Incoming
                </span>
                <span style={{ fontSize: 'clamp(0.5rem, 0.75vw, 0.62rem)', letterSpacing: '0.24em', color: 'var(--color-stone)' }}>
                  ▸ source {String(i + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}
                </span>
              </div>

              {/* Decoding quote — hero scale, left-aligned, bright */}
              <p
                className="voice-quote font-display font-semibold leading-[1.14] opacity-0"
                style={{ fontSize: 'clamp(1.5rem, 4vw, 3.5rem)', color: 'var(--color-light)', letterSpacing: '-0.02em', textWrap: 'balance', filter: 'blur(var(--vb, 0px))' }}
                dangerouslySetInnerHTML={{ __html: quotesHTML[i] }}
              />

              {/* Source readout — signal-strength meter + identity */}
              <div className="voice-author flex items-center justify-center lg:justify-start gap-5 mt-11 opacity-0">
                <div className="flex items-end gap-1" aria-hidden="true">
                  {meterBars(testimonial.name).map((hgt, b) => (
                    <span key={b} style={{ width: 3, height: `${hgt * 22}px`, background: b < 4 ? 'var(--color-fire)' : 'var(--color-ash)', display: 'inline-block' }} />
                  ))}
                </div>
                <div style={{ borderLeft: '1px solid var(--color-border)', paddingLeft: '1.25rem' }}>
                  <p className="font-display font-bold uppercase" style={{ fontSize: 'clamp(0.9rem, 1.3vw, 1.2rem)', color: 'var(--color-light)', letterSpacing: '0.01em', lineHeight: 1.1 }}>{testimonial.name}</p>
                  <p className="font-mono uppercase" style={{ fontSize: 'clamp(0.58rem, 0.9vw, 0.72rem)', color: 'var(--color-stone)', letterSpacing: '0.16em', marginTop: 5 }}>{testimonial.title}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
